import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Footprints, MapPin, Timer, Target, TrendingUp, Calendar,
  Save, Settings, Award, Flame, ChevronRight, BarChart3, ArrowLeft
} from 'lucide-react';

const API = '/api';

export default function ActivityTracker() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todayActivity, setTodayActivity] = useState(null);
  const [history, setHistory] = useState([]);
  const [goals, setGoals] = useState({ daily_steps: 6000, daily_walking_km: 3.0, daily_running_km: 0 });
  const [showGoalSettings, setShowGoalSettings] = useState(false);

  // Form state
  const [steps, setSteps] = useState('');
  const [walkingKm, setWalkingKm] = useState('');
  const [runningKm, setRunningKm] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [todayRes, historyRes, goalsRes] = await Promise.all([
        axios.get(`${API}/activity/today`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/activity/history?days=7`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/activity/goals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (todayRes.data.has_activity) {
        setTodayActivity(todayRes.data);
        const a = todayRes.data.activity;
        setSteps(a.steps?.toString() || '');
        setWalkingKm(a.walking_km?.toString() || '');
        setRunningKm(a.running_km?.toString() || '');
        setNotes(a.notes || '');
      }

      setHistory(historyRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/activity/log`,
        {
          steps: steps ? parseInt(steps) : null,
          walking_km: walkingKm ? parseFloat(walkingKm) : null,
          running_km: runningKm ? parseFloat(runningKm) : null,
          notes: notes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTodayActivity(response.data);
      toast.success('Aktivite kaydedildi! 💪');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGoals = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await axios.post(
        `${API}/activity/goals`,
        goals,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Hedefler güncellendi!');
      setShowGoalSettings(false);
    } catch (error) {
      toast.error('Hedef güncelleme başarısız');
    }
  };

  const getProgress = (current, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(100, Math.round((current || 0) / goal * 100));
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-blue-50 via-white to-blue-50">
      {/* Header */}
      <section className="py-8 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Footprints className="h-10 w-10" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-center mb-2">Aktivite Takibi</h1>
          <p className="text-center text-white/90">Günlük adım, yürüyüş ve koşu aktivitelerinizi kaydedin</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Tabs defaultValue="today" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="today">Bugün</TabsTrigger>
            <TabsTrigger value="history">Haftalık</TabsTrigger>
          </TabsList>

          {/* Today Tab */}
          <TabsContent value="today" className="space-y-6">
            {/* Progress Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <Footprints className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <div className="text-2xl font-bold">{steps || 0}</div>
                  <div className="text-sm text-gray-500">/ {goals.daily_steps} adım</div>
                  <Progress value={getProgress(parseInt(steps) || 0, goals.daily_steps)} className="mt-2 h-2" />
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <MapPin className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <div className="text-2xl font-bold">{walkingKm || 0}</div>
                  <div className="text-sm text-gray-500">/ {goals.daily_walking_km} km yürüyüş</div>
                  <Progress value={getProgress(parseFloat(walkingKm) || 0, goals.daily_walking_km)} className="mt-2 h-2" />
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <Flame className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                  <div className="text-2xl font-bold">{runningKm || 0}</div>
                  <div className="text-sm text-gray-500">km koşu</div>
                  <Progress value={getProgress(parseFloat(runningKm) || 0, goals.daily_running_km || 5)} className="mt-2 h-2" />
                </CardContent>
              </Card>
            </div>

            {/* Input Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Bugünkü Aktivite</span>
                  <Button variant="ghost" size="sm" onClick={() => setShowGoalSettings(!showGoalSettings)}>
                    <Settings className="h-4 w-4 mr-1" />
                    Hedefler
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Goal Settings */}
                {showGoalSettings && (
                  <Card className="bg-gray-50 mb-4">
                    <CardContent className="pt-4 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Günlük Adım Hedefi</Label>
                          <Input
                            type="number"
                            value={goals.daily_steps}
                            onChange={(e) => setGoals({...goals, daily_steps: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Yürüyüş Hedefi (km)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={goals.daily_walking_km}
                            onChange={(e) => setGoals({...goals, daily_walking_km: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Koşu Hedefi (km)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={goals.daily_running_km}
                            onChange={(e) => setGoals({...goals, daily_running_km: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                      <Button size="sm" onClick={handleSaveGoals}>Hedefleri Kaydet</Button>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Adım Sayısı</Label>
                    <Input
                      type="number"
                      placeholder="6000"
                      value={steps}
                      onChange={(e) => setSteps(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Yürüyüş (km)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="3.0"
                      value={walkingKm}
                      onChange={(e) => setWalkingKm(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Koşu (km)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="0"
                      value={runningKm}
                      onChange={(e) => setRunningKm(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <Label>Not (opsiyonel)</Label>
                  <Input
                    placeholder="Bugün parkta yürüyüş yaptım..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>

                <Button onClick={handleSave} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700">
                  {saving ? 'Kaydediliyor...' : <><Save className="h-4 w-4 mr-2" /> Kaydet</>}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            {/* Weekly Summary */}
            {history.summary && (
              <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                    Haftalık Özet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{history.summary.total_steps?.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">Toplam Adım</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">{history.summary.total_walking_km}</div>
                      <div className="text-sm text-gray-500">km Yürüyüş</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">{history.summary.total_running_km}</div>
                      <div className="text-sm text-gray-500">km Koşu</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{history.summary.days_logged}</div>
                      <div className="text-sm text-gray-500">Gün Kayıt</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trend Chart (Simple) */}
            {history.trend && history.trend.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                    Haftalık Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between h-32 gap-2">
                    {history.trend.map((day, idx) => {
                      const maxSteps = Math.max(...history.trend.map(d => d.steps || 0)) || 1;
                      const height = ((day.steps || 0) / maxSteps) * 100;
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center">
                          <div
                            className="w-full bg-blue-500 rounded-t transition-all"
                            style={{ height: `${Math.max(height, 5)}%` }}
                          />
                          <span className="text-xs text-gray-500 mt-1">
                            {new Date(day.date).toLocaleDateString('tr-TR', { weekday: 'short' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Activity List */}
            <Card>
              <CardHeader>
                <CardTitle>Son Aktiviteler</CardTitle>
              </CardHeader>
              <CardContent>
                {history.activities && history.activities.length > 0 ? (
                  <div className="space-y-3">
                    {history.activities.map((activity, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium">
                            {new Date(activity.date).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'short' })}
                          </div>
                          <div className="text-sm text-gray-500">
                            {activity.steps?.toLocaleString()} adım • {activity.walking_km || 0} km
                          </div>
                        </div>
                        <Badge variant={activity.steps >= goals.daily_steps ? 'default' : 'secondary'}>
                          {activity.steps >= goals.daily_steps ? '✓ Hedef' : `${getProgress(activity.steps, goals.daily_steps)}%`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Henüz aktivite kaydı yok</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
